import { Component, OnDestroy, OnInit, Inject, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { TranslateService } from '@ngx-translate/core';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { DataService } from './services/data.service';
import { navigation } from 'app/navigation/navigation';
import { AclService } from 'ng2-acl/dist';
import { fuseAnimations } from '@fuse/animations';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { UploadEvent, UploadFile, FileSystemDirectoryEntry, FileSystemFileEntry } from 'ngx-file-drop';

import { locale as navigationEnglish } from './navigation/i18n/en';
import { locale as navigationRussian } from './navigation/i18n/ru';
import { UserModel } from './models';


@Component({
    selector   : 'fuse-root',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss']
})
export class AppComponent implements OnDestroy
{
    currentUser: UserModel;

    constructor(
        private fuseNavigationService: FuseNavigationService,
        private fuseSplashScreen: FuseSplashScreenService,
        private fuseTranslationLoader: FuseTranslationLoaderService,
        private dataService: DataService,
        private aclService: AclService,
        public dialog: MatDialog, 
    )
    {
        this.fuseTranslationLoader.loadTranslations(navigationEnglish, navigationRussian);

        this.dataService.isLoggedIn.subscribe((loggedIn) => {
            if (loggedIn) {
                this.currentUser = this.dataService.getProfileData();
                if (this.currentUser.id === 1) {
                    const nav: any = navigation;
                    nav.push({
                        'id'      : 'partnership',
                        'title'   : 'Partnership',
                        'translate': 'NAV.PARTNERSHIP.TITLE',
                        'type'    : 'group',
                        'children': [
                            {
                                'id'   : 'payment-rates',
                                'title': 'Payment rates',
                                'translate': 'NAV.PAYMENTS_RATES.TITLE',
                                'type' : 'item',
                                'icon' : 'attach_money',
                                'url': '/payment-rates'
                            }
                        ]
                    });
                }

                const mainImportExport = {
                    'id'      : 'import-export',
                    'title'   : 'Import Export',
                    'translate': 'NAV.CONTACTS.TITLE',
                    'type'    : 'group',
                    'children': []
                };
                if (this.hasAccess('view_common_other_contactsImport')) {
                    mainImportExport.children.push({
                        'id'   : 'import',
                        'title': 'Import',
                        'translate': 'NAV.IMPORT.TITLE',
                        'type' : 'item',
                        'icon' : 'file_download',
                        'function': () => {
                            this.openImportDialog();
                        }
                    });
                }
                if (this.hasAccess('view_common_other_contactsExport')) {
                    mainImportExport.children.push({
                        'id'   : 'export',
                        'title': 'Export',
                        'translate': 'NAV.EXPORT.TITLE',
                        'type' : 'item',
                        'icon' : 'import_export',
                        'function': () => {
                            this.openExportDialog();
                        }
                    });
                }
                if (this.hasAccess('view_common_other_contactsImport') || this.hasAccess('view_common_other_contactsExport')) {
                    navigation.push(mainImportExport);
                }
            } else {
                this.removeItems('partnership', 'import-export');
            }
          });

    }

    removeItems(...navItems) {
        navItems.forEach(navItem => {
            const idx = navigation.findIndex(item => item.id === navItem);
            if (idx !== -1) {
                navigation.splice(idx, 1);
            }
        });
    }

    ngOnDestroy() {
        this.dataService.socketDisconnect();
    }

    openImportDialog() {
        const dialogRef = this.dialog.open(ImportDialogComponent, {
            disableClose: false
        });
    }

    openExportDialog() {
        const dialogRef = this.dialog.open(ExportDialogComponent, {
            width: '60%'
        });
    }

    hasAccess(access) {
        return this.aclService.can(access);
      }
}

@Component({
    selector: 'bfb-dialog',
    templateUrl: './export-dialog.component.html',
    animations: fuseAnimations
  })
  export class ExportDialogComponent {
  
    exportForm: FormGroup;
    clearData = false;
    translationObj: any = {};
  
    constructor(
      public dialogRef: MatDialogRef<ExportDialogComponent>,
      @Inject(MAT_DIALOG_DATA) public data: any,
      private dataService: DataService,
      private fb: FormBuilder,
      private translate: TranslateService
    ) { 
        const from = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD');
        const to = moment(new Date()).format('YYYY-MM-DD');
        this.exportForm = this.fb.group({
          'from':  [from, Validators.required],
          'to': [to, Validators.required],
          'type': ['', Validators.required]
        });
        this.translate.get('DIALOGS').subscribe(res => {
            this.translationObj = res;
        });
    }

    exportLeads(event, form) {
        form.from = moment(form.from).format('YYYY-MM-DD');
        form.to = moment(form.to).format('YYYY-MM-DD');
        this.dataService.exportLeads(form).subscribe((res: any) => {
            if (res && !res.errors) {
                this.downloadFile(res.data.base64, this.exportForm.value);
                this.exportForm.reset();
                this.dialogRef.close();
            } else {
                if (res.status_code === 404) {
                    this.dataService.showAlert.next({type: 'danger', message: this.translationObj.EXPORT_ERR});
                    this.dialogRef.close(true);
                }
            }
        });
    }

    downloadFile(data, form) {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;base64,' + data);
        const exportType = form.type === 'contacts' ? 'Contacts-' : 'Leads-';
        element.setAttribute('download', 'Export-' + exportType + 'from-' + moment(form.from).format('YYYY-MM-DD') + '-to-' + moment(form.to).format('YYYY-MM-DD') + '.csv');
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }
  
    onNoClick(event): void {
      event.preventDefault();
      this.exportForm.reset();
      this.dialogRef.close();
    }
  }
  

@Component({
    selector   : 'bfb-view-document',
    templateUrl: './import-dialog.component.html',
    styles: [
        `.customStyle{
            border: 1px dashed #e5e5e5 !important;
            border-radius: 2px;
        }

        ::ng-deep .content {
            border: 1px dashed #e5e5e5 !important;
            border-radius: 2px;
            padding: 20px;
                span{
                    color: rgba(0, 0, 0, 0.54);
                }
                a {
                    cursor: pointer;
                }
        }
        
        
        .upload-res{
            display: flex;
            justify-content: center;
        }
        .file-name p {
            text-align: center;
        }
        
        .mat-dialog-container{
            padding: 0 !important;
        }`
    ]
})
export class ImportDialogComponent {

    files: UploadFile[] = [];
    fileName: string;
    addDocumentForm: FormGroup;
    fileUploadedSubj: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    fileUploaded = false;
    uploadInProgress = false;
    formData = new FormData();
    translationObj: any = {};

    constructor(
        public dialogRef: MatDialogRef<ImportDialogComponent>, 
        private fb: FormBuilder, 
        private dataService: DataService, 
        private zone: NgZone,
        private translate: TranslateService) {
        this.translate.get('DIALOGS').subscribe(res => {
            this.translationObj = res;
        });

        this.addDocumentForm = fb.group({
            'image':  ['', Validators.required]
        });
        
        this.fileUploadedSubj.subscribe(res => {
            this.fileUploaded = res;
        });
    }

    dropped(event: UploadEvent) {
        this.files = event.files;
        for (const droppedFile of event.files) {
          if (droppedFile.fileEntry.isFile) {
            const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
            fileEntry.file((file: File) => {
                this.zone.run(() => {
                    this.fileName = file.name;
                });
                this.formData.append('file', file);
                this.zone.run(() => {
                    this.fileUploadedSubj.next(true);
                });
            });
          }
        }
    }

    fileInputChange(event) {
        const file = event.target.files[0];
        this.zone.run(() => {
            this.fileName = file.name;
        });
        this.formData.append('file', file);
        this.fileUploadedSubj.next(true);
    }

    importLeads() {
        this.uploadInProgress = true;
        this.dataService.importLeads(this.formData).subscribe((res: any) => {
            if (res && !res.errors) {
                this.uploadInProgress = false;
                this.translate.get('DIALOGS.SUCCESS_IMPORT', { success: res.data.success, total: res.data.total, exists: res.data.exists }).subscribe(msg => {
                    this.dataService.showAlert.next({type: 'success', message: msg});
                });
                this.dialogRef.close(true);
            } else {
                this.uploadInProgress = false;
                if (res.status_code === 422) {
                    this.dataService.showAlert.next({type: 'danger', message: this.translationObj.IMPORT_ERR});
                }
            }
        },
        (err) => {
            this.uploadInProgress = false;
            this.dialogRef.close(false);
        });
    }

    fileOver(event){}

    fileLeave(event){}

    close() {
        this.dialogRef.close();
    }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts